import { Component, EventListener, findComponents } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { getCurrentViewport, VIEWPORT_ENTERED } from '@gondel/plugin-media-queries';
import { Image } from '@swisslife/patterns-base/src/atoms/image/js/image';
import '@swisslife/patterns-base/src/atoms/image/js/image';

enum Selectors {
	FooterArea = '.js-o-footer__area',
	ContentWrapper = '.js-o-footer__content-wrapper',
}

enum States {
	DisableAnimation = 'state-h-animation--disabled',
	AnimationRunning = 'state-o-footer--animation-running',
}

// The @Component decorator will connect the class with `data-g-name="Footer"` elements.
@Component('Footer')
export class Footer extends GondelJqueryComponent {
	public $footerArea: JQuery;
	public $contentWrapper: JQuery;

	private footerHeight: number;
	private uxBehaviourType;
	private scrollMagicController: any;
	private sceneFooter: any;

	public start() {
		this.$footerArea = this.$ctx.find(Selectors.FooterArea);
		this.$contentWrapper = this.$ctx.find(Selectors.ContentWrapper);

		const currentVp = getCurrentViewport();
		this.updateUxBehaviourType(currentVp);
		this.addImageLoadedHandler();
	}

	public async sync() {
		// check if animation should be enabled
		if (!$('body').hasClass(States.DisableAnimation) && this.uxBehaviourType === 'large') {
			await this.initFooterAnimation();
		}
	}

	public stop() {
		this.destroyFooterAnimation();
	}

	@EventListener(VIEWPORT_ENTERED)
	public _handleViewportChangeEvent(event) {
		const previousUxBehaviourType = this.uxBehaviourType;
		this.updateUxBehaviourType(event.viewport);

		// if the viewport change was from small to medium or vice versa, we destroy animation for small devices
		if (previousUxBehaviourType !== this.uxBehaviourType) {
			if (this.uxBehaviourType === 'small') {
				this.destroyFooterAnimation();
			} else {
				this.initFooterAnimation();
			}
		}
	}

	/**
	 * Add an image loaded handler to adjust the outer padding after possible image(s) finished with loading
	 */
	private addImageLoadedHandler() {
		const imageComponents = findComponents<Image>(this._ctx, 'Image');
		if (imageComponents.length > 0 && this.uxBehaviourType === 'large') {
			for (const imageComponent of imageComponents) {
				const promise = imageComponent.getImageLoadedPromise();
				promise.then(() => {
					this.updateOuterPadding();
					this.updateSceneDuration();
				});
			}
		}
	}

	/**
	 * Initialize the footer animation
	 */
	private async initFooterAnimation() {
		if (this.$footerArea.length < 1) {
			return;
		}

		// tslint:disable
		const {
			TimelineMax,
			TweenMax,
			Linear,
			ScrollMagic,
		} = (await import(/* webpackChunkName: "dynamic-imports/animation-libs" */ '@swisslife/shared-ui/src/utils/animations/js/animation-libs')) as any;
		// tslint:enable

		this.updateOuterPadding();

		this.scrollMagicController = new ScrollMagic.Controller();
		const timeline = new TimelineMax();

		this.sceneFooter = new ScrollMagic.Scene({
			triggerElement: Selectors.ContentWrapper,
			triggerHook: 'onEnter',
			duration: this.footerHeight,
		}); // scene1.addIndicators()

		const tween = TweenMax.to(this.$footerArea, 1, { y: 0, opacity: 1, ease: Linear.easeIn });

		timeline.add(tween);
		timeline.eventCallback('onStart', () => {
			this.$ctx.addClass(States.AnimationRunning);
		});
		timeline.eventCallback('onReverseComplete', () => {
			this.$ctx.removeClass(States.AnimationRunning);
		});

		this.sceneFooter.setTween(timeline);

		this.scrollMagicController.addScene([this.sceneFooter]);

		$(window).on('resize.footerAnimation', () => {
			this.updateOuterPadding();
			this.updateSceneDuration();
		});
	}

	/**
	 * Update the scene duration
	 */
	private updateSceneDuration() {
		if (this.sceneFooter) {
			this.sceneFooter.duration(this.footerHeight);
		}
	}

	/**
	 * Update the content margin upon init or resize
	 */
	private updateOuterPadding() {
		if (this.uxBehaviourType === 'large') {
			this.footerHeight = this.$footerArea.outerHeight() || 0;
			this.$contentWrapper.css('padding-bottom', this.footerHeight);
		}
	}

	/**
	 * Destroy the footer animation
	 */
	private destroyFooterAnimation() {
		this.scrollMagicController.destroy(true);
		this.$footerArea.removeAttr('style');
		this.$contentWrapper.removeAttr('style');

		$(window).off('resize.footerAnimation');
	}

	/**
	 * Update the uxBehaviourType variable
	 * @param viewport {string}		the new viewport
	 */
	private updateUxBehaviourType(viewport: string) {
		if (viewport === 'medium' || viewport === 'large' || viewport === 'xlarge' || viewport === 'xxlarge') {
			this.uxBehaviourType = 'large';
		} else {
			this.uxBehaviourType = 'small';
		}
	}
}
